import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './SuggestedDestinationForm.css';

const SuggestedDestinationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    city: '',
    state: '',
    country: 'India',
    cityLatitude: '',
    cityLongitude: '',
    shortDescription: '',
    displayOrder: 0,
    isActive: true
  });

  const [logo, setLogo] = useState(null);
  const [existingLogo, setExistingLogo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cityInput, setCityInput] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetchDestination();
    }
  }, [id]);

  const fetchDestination = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/suggested-destinations/${id}`);
      const destination = response.data.data;
      
      setFormData({
        city: destination.city || '',
        state: destination.state || '',
        country: destination.country || 'India',
        cityLatitude: destination.cityLatitude || '',
        cityLongitude: destination.cityLongitude || '',
        shortDescription: destination.shortDescription || '',
        displayOrder: destination.displayOrder || 0,
        isActive: destination.isActive !== undefined ? destination.isActive : true
      });
      
      setExistingLogo(destination.logo || '');
      setCityInput(destination.city || '');
    } catch (error) {
      setError('Failed to fetch destination details');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    });
  };

  const handleCityInputChange = async (e) => {
    const value = e.target.value;
    setCityInput(value);
    setFormData({
      ...formData,
      city: value
    });

    if (value.length > 2) {
      try {
        const response = await axios.get(`${API_BASE_URL}/public/place/autocomplete`, {
          params: { input: value }
        });
        setAddressSuggestions(response.data.data || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
        if (error.response?.status !== 400) {
          setAddressSuggestions([]);
        }
      }
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePlaceSelect = async (place) => {
    setCityInput(place.description);
    setFormData({
      ...formData,
      city: place.description
    });
    setShowSuggestions(false);
    setAddressSuggestions([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/public/place/details`, {
        params: { placeId: place.place_id }
      });
      
      const placeDetails = response.data.data;
      setFormData({
        ...formData,
        city: place.description,
        state: placeDetails.state || formData.state,
        country: placeDetails.country || formData.country,
        cityLatitude: placeDetails.latitude || formData.cityLatitude,
        cityLongitude: placeDetails.longitude || formData.cityLongitude
      });
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  };

  const handleLogoChange = (e) => {
    setLogo(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = new FormData();
      
      submitData.append('city', formData.city);
      submitData.append('state', formData.state);
      submitData.append('country', formData.country);
      submitData.append('cityLatitude', formData.cityLatitude);
      submitData.append('cityLongitude', formData.cityLongitude);
      submitData.append('shortDescription', formData.shortDescription);
      submitData.append('displayOrder', formData.displayOrder);
      submitData.append('isActive', formData.isActive);

      if (logo) {
        submitData.append('logo', logo);
      }

      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/suggested-destinations/${id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_BASE_URL}/admin/suggested-destinations`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      navigate('/sy-admin/suggested-destinations');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save destination');
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container">
        <div className="destination-form-header">
          <h1>{isEdit ? 'Edit Suggested Destination' : 'Add New Suggested Destination'}</h1>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="destination-form">
          <div className="form-section">
            <h2>Basic Information</h2>
            
            <div className="form-group" style={{ position: 'relative' }}>
              <label>City Name *</label>
              <input
                type="text"
                name="city"
                value={cityInput || formData.city}
                onChange={handleCityInputChange}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                required
                placeholder="Enter city name..."
              />
              {showSuggestions && addressSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  marginTop: '4px'
                }}>
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handlePlaceSelect(suggestion)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: 500, color: '#0f172a' }}>
                        {suggestion.structured_formatting?.main_text || suggestion.description}
                      </div>
                      {suggestion.structured_formatting?.secondary_text && (
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                          {suggestion.structured_formatting.secondary_text}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <small className="form-hint">Start typing to get city suggestions. This will auto-fill coordinates.</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City Latitude *</label>
                <input
                  type="number"
                  name="cityLatitude"
                  value={formData.cityLatitude}
                  onChange={handleChange}
                  step="any"
                  required
                  placeholder="e.g., 25.2138"
                />
                <small className="form-hint">City center latitude (auto-filled from city selection)</small>
              </div>
              <div className="form-group">
                <label>City Longitude *</label>
                <input
                  type="number"
                  name="cityLongitude"
                  value={formData.cityLongitude}
                  onChange={handleChange}
                  step="any"
                  required
                  placeholder="e.g., 75.8648"
                />
                <small className="form-hint">City center longitude (auto-filled from city selection)</small>
              </div>
            </div>

            <div className="form-group">
              <label>Short Description</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                rows="3"
                placeholder="Brief description of the destination (shown in search screen)"
                maxLength={200}
              />
              <small className="form-hint">Max 200 characters</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  min="0"
                />
                <small className="form-hint">Lower numbers appear first</small>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="isActive" value={formData.isActive} onChange={handleChange}>
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Logo</h2>
            
            {existingLogo && (
              <div className="logo-preview">
                <img 
                  src={`${API_BASE_URL_WITHOUT_API}${existingLogo}`} 
                  alt="Current logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <p>Current Logo</p>
              </div>
            )}

            <div className="form-group">
              <label>{existingLogo ? 'Upload New Logo' : 'Upload Logo'}</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
              />
              {logo && (
                <div className="new-logo-preview">
                  <p>New logo selected: {logo.name}</p>
                </div>
              )}
              <small className="form-hint">Recommended: Square image, max 2MB (JPG, PNG, GIF, WebP, SVG)</small>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Update Destination' : 'Create Destination'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/suggested-destinations')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default SuggestedDestinationForm;

